import { useState, useEffect } from 'react';
import { getValidToken } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function DBMonitor() {
  const [tables, setTables] = useState([]);
  const [tableData, setTableData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState({});

  useEffect(() => {
    fetchTables();
  }, []);

  async function fetchTables() {
    try {
      const token = await getValidToken();
      const res = await fetch(`${API_URL}/api/admin/db/tables`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch tables');
      const data = await res.json();
      setTables(data);

      // Initialize all tables as collapsed
      const initialCollapsed = {};
      data.forEach((t) => (initialCollapsed[t.name] = true));
      setCollapsed(initialCollapsed);

      // Fetch data for all tables
      const dataMap = {};
      await Promise.all(
        data.map(async (t) => {
          const r = await fetch(
            `${API_URL}/api/admin/db/table-data?name=${encodeURIComponent(t.name)}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (r.ok) dataMap[t.name] = await r.json();
        })
      );
      setTableData(dataMap);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function toggleCollapse(name) {
    setCollapsed((prev) => ({ ...prev, [name]: !prev[name] }));
  }

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">DB Monitor</h1>
        <div className="text-gray-500">Loading tables...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">DB Monitor</h1>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">DB Monitor</h1>
      <div className="space-y-4">
        {tables.map((table) => {
          const data = tableData[table.name];
          const isCollapsed = collapsed[table.name];
          return (
            <div key={table.name} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {/* Header */}
              <button
                onClick={() => toggleCollapse(table.name)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <svg
                    className={`w-4 h-4 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="font-semibold text-gray-900">{table.name}</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>{table.row_count} rows</span>
                  <span>{table.size}</span>
                  <span>{table.indexes.length} indexes</span>
                </div>
              </button>

              {/* Table data */}
              {!isCollapsed && data && (
                <div className="border-t border-gray-200">
                  {/* Index list */}
                  {table.indexes.length > 0 && (
                    <div className="px-5 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
                      Indexes: {table.indexes.join(', ')}
                    </div>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50">
                          {data.columns.map((col) => (
                            <th key={col} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {data.rows.length === 0 ? (
                          <tr>
                            <td colSpan={data.columns.length} className="px-4 py-3 text-center text-gray-400">
                              No data
                            </td>
                          </tr>
                        ) : (
                          data.rows.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              {row.map((cell, j) => (
                                <td key={j} className="px-4 py-2 text-gray-700 whitespace-nowrap max-w-xs truncate">
                                  {cell === null ? <span className="text-gray-300">NULL</span> : String(cell)}
                                </td>
                              ))}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
